class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.renderedQuery = '';
    this.input = this.querySelector('input[type="search"]');
    this.isOpen = false;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.quickSearchTrendingProduct = this.querySelector('[data-quick-trendingProduct]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', () => {
      clearTimeout(this.inputTimer);
      this.abortController?.abort();
      this.removeAttribute('loading');
      this.resetSelection();
      if (!this.getQuery().length) {
        this.close(true);
        return;
      }
      this.inputTimer = setTimeout(() => this.onChange(), 300);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  disconnectedCallback() {
    clearTimeout(this.inputTimer);
    this.abortController?.abort();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.contains(document.activeElement)) this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
    else this.input.value = this.getQuery();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true' && this.renderedQuery === searchTerm) {
      this.open();
      this.setShowPredictive();
    } else {
      this.getSearchResults(searchTerm);
      this.setShowPredictive();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (event.target !== this.input) return;
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    if (event.target !== this.input) return;
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
    if (event.code === 'Enter' && this.isOpen && this.renderedQuery === this.getQuery() && this.predictiveSearchResults.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
    if (event.code === 'Escape') this.close();
  }

  switchOption(direction) {
    if (!this.getAttribute('open') || this.renderedQuery !== this.getQuery()) return;

    const moveUp = direction === 'up';
    const allElements = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')];
    if (!allElements.length) return;
    const selectedElement = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
    const selectedIndex = allElements.indexOf(selectedElement);
    const nextIndex = selectedIndex < 0 ? (moveUp ? allElements.length - 1 : 0) : (selectedIndex + (moveUp ? -1 : 1) + allElements.length) % allElements.length;
    const activeElement = allElements[nextIndex];

    this.statusElement.textContent = '';

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    if (this.renderedQuery !== this.getQuery()) return;
    const selectedProduct = this.predictiveSearchResults.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    return fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        if (controller.signal.aborted || searchTerm !== this.getQuery() || !this.contains(document.activeElement)) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Missing predictive search section');
        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch(() => {
        if (controller.signal.aborted || this.abortController !== controller) return;
        this.close();
        this.removeAttribute('results');
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.renderedQuery = searchTerm;
    const prefix = this.input.id;
    const results = this.predictiveSearchResults.querySelector('.predictiveSearchResults');
    const list = this.predictiveSearchResults.querySelector('[role="listbox"]');
    const heading = this.predictiveSearchResults.querySelector('.predictive-search__heading');
    if (prefix && list && heading) {
      if (results) results.id = `${prefix}-results`;
      list.id = `${prefix}-results-list`;
      heading.id = `${prefix}-results-heading`;
      list.setAttribute('aria-labelledby', heading.id);
      this.input.setAttribute('aria-controls', list.id);
      this.input.setAttribute('aria-owns', list.id);
      list.querySelectorAll('[role="option"]').forEach((option, index) => {
        option.id = `${prefix}-option-${index}`;
      });
    }
    this.resetSelection();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.setShowPredictive();
    this.setOthers();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    clearTimeout(this.inputTimer);
    this.abortController?.abort();
    this.removeAttribute('loading');
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.renderedQuery = '';
    }
    this.setHiddenPredictive();
    this.resetSelection();
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }

  resetSelection() {
    this.predictiveSearchResults.querySelectorAll('[aria-selected="true"]').forEach(option => option.setAttribute('aria-selected', false));
    this.input.setAttribute('aria-activedescendant', '');
  }

  setShowPredictive() {
    this.predictiveSearchResults.classList.add('is-show');
    this.predictiveSearchResults.classList.remove('is-hidden');
    this.quickSearchTrendingProduct.classList.remove('is-show');
    this.quickSearchTrendingProduct.classList.add('is-hidden');
  }

  setHiddenPredictive() {
    this.predictiveSearchResults.classList.add('is-hidden');
    this.predictiveSearchResults.classList.remove('is-show');
    this.quickSearchTrendingProduct.classList.add('is-show');
    this.quickSearchTrendingProduct.classList.remove('is-hidden');
  }

  setOthers() {
    if(window.product_swatch_style == 'slider'){
        let productList = this.querySelectorAll('.product');

        if(productList.length > 0) {
            productList.forEach((element) => {
                var product = $(element),
                    productSwatch = product.find('.card-swatch--slider');
                
                if(productSwatch.length > 0){
                    var swatchGrid = productSwatch.find('.swatch');

                    if(swatchGrid.length > 0){
                        if(!swatchGrid.hasClass('slick-initialized')){
                            Shopify.ProductSwatchs.showSwatchSlider({
                                slider: swatchGrid,
                                onComplete: null
                            });
                        }
                    }
                }
            });
        }
    }

    if(window.compare.show){
        Shopify.ProductCompare.setLocalStorageProductForCompare();
    }

    if(window.wishlist.show){
        Shopify.ProductWishlist.setLocalStorageProductForWishlist();
    }

    if (this.checkNeedToConvertCurrency()) {
        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');

        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
    }
  }

  checkNeedToConvertCurrency() {
    return (window.show_multiple_currencies && Currency.currentCurrency != window.shop_currency) || window.show_auto_currency;
}
}

customElements.define('predictive-search', PredictiveSearch);
